import { AfterViewChecked, AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, SecurityContext, ViewChild } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { FilterMetadata, MessageService, SelectItem, SortEvent, TableState } from 'primeng/api';
import { Sidebar } from 'primeng/sidebar';
import { Table } from 'primeng/table';
import { BehaviorSubject, forkJoin, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { AuthUserType } from '../auth/auth.types';
import { EventService } from '../services/event.service';
import { FilterService } from '../services/filter.service';
import { LookupService } from '../services/lookup.service';
import { PreviousRouteService } from '../services/previous-route.service';
import { AppConstants } from '../shared/app.constants';
import { AppPermissions } from '../shared/app.permissions';
import { DateRangeFilterComponent } from '../shared/components/date-range-filter/date-range-filter.component';
import { GeographicFilterComponent } from '../shared/components/geographic-filter/geographic-filter.component';
import { DateRangeFilterModel } from '../shared/models/date-range-filter-model';
import { GeographicFilterModel } from '../shared/models/geographic-filter-model';

@Component({
  selector: 'ness-events',
  templateUrl: './events.component.html'
})
export class EventsComponent implements OnDestroy, OnInit, AfterViewChecked, AfterViewInit {
  private _destroyed$: Subject<void> = new Subject<void>();
  private _crimeTypes$: Observable<ServerModels.ENESS_REF_CrimeTypes[]> = this.lookupService.getAllCrimeTypes();
  canCreateEvent = AppPermissions.canCreateEvent;
  canViewEventFilters = AppPermissions.canViewEventFilters;
  cols: any[];
  crimeTypeName: string;
  crimeTypes$: Observable<any[]>;
  event: ServerModels.ENESS_EventDTO;
  events: ServerModels.EventViewModel[] = [];
  eventNarrative$: Observable<string>;
  eventNarrative: any = {};
  loading: boolean;
  loadingEndRow: number = AppConstants.dbPagingDefaultRows;
  loadingStartRow: number = 1;
  loadingStartRow$: BehaviorSubject<number> = new BehaviorSubject<number>(this.loadingStartRow);
  loadingEndRow$: BehaviorSubject<number> = new BehaviorSubject<number>(this.loadingEndRow);
  messageTemplate: string;
  openViewDetailForm: boolean = false;
  rows: number = AppConstants.defaultTableRows;
  selectedEvent: ServerModels.EventViewModel;
  showEventNarrative: boolean = false;
  readonly gridStateKey: string = 'eventsGridState'
  readonly eventsStorageKey: string = 'eventsFilterModel';
  readonly geoFilterStorageKey: string = 'geoFilterStorageKeyEvents';
  dateFilterModel: DateRangeFilterModel = null;
  geographicFilter: GeographicFilterModel = null;
  @ViewChild(Table, { static: false }) dataTable: Table;
  @ViewChild(DateRangeFilterComponent, { static: false }) dateRangeFilterCmp: DateRangeFilterComponent;
  @ViewChild(GeographicFilterComponent, { static: false }) geoFilterCmp: GeographicFilterComponent;
  readonly hasUserType: AuthUserType[] = ['ATFFieldDivision', 'ATFHQ'];
  hasEditPermission: boolean = false;
  securityContext = SecurityContext;

  constructor(public messageService: MessageService,
    public domSanitizer: DomSanitizer,
    private eventService: EventService,
    private lookupService: LookupService,
    private authService: AuthService,
    private filterService: FilterService,
    private previousRouteService: PreviousRouteService,
    private router: Router,
    private changeDetectorRef: ChangeDetectorRef) { }

  ngOnInit() {
    this.loading = false;
    this.crimeTypes$ = this._crimeTypes$.pipe(
      takeUntil(this._destroyed$),
      switchMap((crimes: ServerModels.ENESS_REF_CrimeTypes[]) => {
        let crimeTypes = crimes.filter((c: ServerModels.ENESS_REF_CrimeTypes) => c.ShowOnDropDown).map((t: ServerModels.ENESS_REF_CrimeTypes) => {
          return { label: t.CrimeTypeName, value: t.CrimeTypeName };
        });
        return of(<SelectItem[]>crimeTypes);
      }));

    this.messageTemplate = 'Loading events...';
    if (!this.authService.hasRole(['NESS Administrator']) && this.authService.hasUserType(['StateLocalLE'])) {
      this.filterService.geographicFilterModel$.next(null);
    }

    this.cols = [
      { field: 'LECaseNumber', header: 'LE case number' },
      { field: 'EventDateOffset', header: 'Event date/time' },
      { field: 'CrimeTypeName', header: 'Crime type' },
      { field: 'AgencyName', header: 'Agency' }
    ]
    this.previousRouteService.returnUrl = '/events';
  }

  ngAfterViewInit(): void {
    this.applyFilters();
  }

  ngAfterViewChecked(): void {
    this.changeDetectorRef.detectChanges();
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  public applyFilters(): void {
    this.dateRangeFilterCmp.applyFilters();
    let storedDateFilter = JSON.parse(sessionStorage.getItem(this.eventsStorageKey));
    this.dateFilterModel = new DateRangeFilterModel(storedDateFilter['_fieldDivisionID'],
      storedDateFilter['_duration'],
      storedDateFilter['_startDate'],
      storedDateFilter['_endDate']);

    if (this.authService.hasRole(['NESS Administrator']) || this.authService.hasUserType(this.hasUserType)) {
      this.geoFilterCmp.applyFilters();
      let storedGeoFilterModel = JSON.parse(sessionStorage.getItem(this.geoFilterStorageKey));
      this.geographicFilter = new GeographicFilterModel(storedGeoFilterModel['_fieldDivisionID'],
        storedGeoFilterModel['_fieldDivisionGroupID'],
        storedGeoFilterModel['_fieldDivisionGroups'],
        storedGeoFilterModel['_stateID'],
        storedGeoFilterModel['_countyID'],
        storedGeoFilterModel['_counties'],
        storedGeoFilterModel['_agencyID'],
        storedGeoFilterModel['_agencies'],
        storedGeoFilterModel['_fieldDivisionName'],
        storedGeoFilterModel['_groupName'],
        storedGeoFilterModel['_groupNameList'],
        storedGeoFilterModel['_stateName'],
        storedGeoFilterModel['_countyName'],
        storedGeoFilterModel['_countyNameList'],
        storedGeoFilterModel['_agencyName'],
        storedGeoFilterModel['_agencyNamesList']
      );

    } else {
      this.filterService.geographicFilterModel$.next(null);
    }

    this.messageTemplate = 'Loading events...';
    this.events = [];
    this._clearLoadingStatus();
    this.getAllEvents(0);
  }

  private _clearLoadingStatus(): void {
    this.loadingStartRow = 1;
    this.loadingEndRow = AppConstants.dbPagingDefaultRows;
    this.loadingStartRow$.next(this.loadingStartRow);
    this.loadingEndRow$.next(this.loadingEndRow);
  }

  public resetDefaultFilters(): void {
    if (!!this.geoFilterCmp && (this.authService.hasRole(['NESS Administrator']) || this.authService.hasUserType(this.hasUserType))) {
      this.geoFilterCmp.resetToDefaultFilters();
    }
    this.dateRangeFilterCmp.resetToDefaultFilters();
  }

  public loadNarrative(eventData: any): void {
    this.eventNarrative$ = this.eventService.getNarrativeById(eventData.eventId).pipe(tap((narrative:string) => {
      this.eventNarrative = {
        LECaseNumber: eventData.caseNumber,
        Narrative: narrative
      }
    }));
    this.showEventNarrative = true;
  }

  public viewDetails(selectedItem: ServerModels.EventViewModel): void {
    this.selectedEvent = selectedItem;
    this.hasEditPermission = false;
    forkJoin({
      event: this.eventService.getEventDataModelById(selectedItem.EventID),
      _hasEditPermission: this.eventService.hasEditPermission(selectedItem.EventID)
    }).pipe(takeUntil(this._destroyed$)).subscribe({
      next: (val: { event: ServerModels.ENESS_EventDTO, _hasEditPermission:boolean }) => {
        this.event = val.event;
        this.hasEditPermission = val._hasEditPermission;
        if (this.event.CrimeTypeID != null) {
          let crimeType: ServerModels.ENESS_REF_CrimeTypes = null;
          this._crimeTypes$.forEach(
            (crime: ServerModels.ENESS_REF_CrimeTypes[]) => {
              crimeType = crime.find((item: ServerModels.ENESS_REF_CrimeTypes) => { return item.CrimeTypeID.toLowerCase() == this.event.CrimeTypeID.toLowerCase() });
            }).then(() => {
              if (crimeType != null)
                this.crimeTypeName = crimeType.CrimeTypeName;
            });
        }
      },
      complete: () => {
        this.openViewDetailForm = true;
      }
    });
  }

  private getAllEvents(pageNumber: number): void {
    this.loading = true;
    let filtersVM = {
      FieldDivisionID: null,
      FieldDivisionGroupIDs: [],
      AgencyName: null,
      StateID: null,
      CountyIDs: [],
      AgencyIDs: [],
      StartDate: this.dateFilterModel.startDate,
      EndDate: this.dateFilterModel.endDate,
      PageNumber: pageNumber,
      RowsPerPage: AppConstants.dbPagingDefaultRows
    } as ServerModels.FiltersViewModel;

    if (this.authService.hasRole(['NESS Administrator']) || this.authService.hasUserType(this.hasUserType)) {
      filtersVM.FieldDivisionID = this.geographicFilter.fieldDivisionID;
      filtersVM.FieldDivisionGroupIDs = this.geographicFilter.fieldDivisionGroups.map(x => x.IntelligenceReportATFReferralGroupsID);
      filtersVM.StateID = this.geographicFilter.stateID;
      filtersVM.CountyIDs = this.geographicFilter.counties.map(x => x.CountyID);
      filtersVM.AgencyIDs = this.geographicFilter.agencies?.map(x => x.AgencyID);
    } else {
      filtersVM.FieldDivisionID = null;
      filtersVM.StateID = null;
      filtersVM.CountyIDs = [];
      filtersVM.FieldDivisionGroupIDs = [];
      filtersVM.AgencyIDs = [];
    }

    let eventGridState = sessionStorage.getItem(this.gridStateKey);
    this.eventService.getAllEventsViewModel(filtersVM).pipe(takeUntil(this._destroyed$)).subscribe(
      (events: ServerModels.EventViewModel[]) => {
        this.loading = events.length > 0;
        if (events.length > 0) {
          let nextPage = pageNumber + 1;
          this.loadingStartRow = pageNumber == 0 ? 1 : (1 + this.events.length);
          this.events = this.events.concat(events);
          this.loadingEndRow = this.events.length;
          this.loadingStartRow$.next(this.loadingStartRow);
          this.loadingEndRow$.next(this.loadingEndRow);
          this.messageTemplate = `Loaded rows ${this.loadingStartRow} thru ${this.loadingEndRow}...`;
          if (this.loading = events.length == AppConstants.dbPagingDefaultRows) {
            this.getAllEvents(nextPage);
          }
          if (eventGridState) {
            this.dataTable.restoreState();
          }
        }
      },
      (err:any) => {
        this.loading = false;
        this.messageService.add({ key: 'eventsMsgs', severity: 'error', summary: 'Error:', detail: err.error });
      });
  }

  public onShowSidebar(sideBar: Sidebar): void {
    window.setTimeout(() => {
      (<HTMLElement>sideBar.el.nativeElement).setAttribute("tabindex", "-1");
      (<HTMLElement>sideBar.el.nativeElement).focus();
    }, 1000);
  }

  public newEvent(): void {
    this.previousRouteService.returnUrl = this.router.url;
    this.eventService.generateEventStagingId().subscribe((newId:string) => this.router.navigate(['event-stepper', { sid: newId, id: '', ipid: '' }], { state: { 'mode': 'Add' } }));
  }

  public eventListCustomSort(sortEvt: SortEvent) {
    if (sortEvt.field == 'EventDateOffset') {
      sortEvt.data.sort((date1, date2) => {
        let value1 = Date.parse(date1[sortEvt.field]);
        let value2 = Date.parse(date2[sortEvt.field]);
        let result: number = null;
        result = value1 - value2;
        return (sortEvt.order * result);
      });
    } else {
      sortEvt.data.sort((data1, data2) => {
        let value1 = data1[sortEvt.field];
        let value2 = data2[sortEvt.field];
        let result = null;
        if (value1 == null && value2 != null)
          result = -1;
        else if (value1 != null && value2 == null)
          result = 1;
        else if (value1 == null && value2 == null)
          result = 0;
        else if (typeof value1 === 'string' && typeof value2 === 'string')
          result = value1.localeCompare(value2);
        else
          result = (value1 < value2) ? -1 : (value1 > value2) ? 1 : 0;

        return (sortEvt.order * result);
      });
    }
  }

  public filterByCrimeType(evt: Event): void {
    if (!(<HTMLSelectElement>evt.target).value) {
      (<HTMLSelectElement>evt.target).value = '';
      delete this.dataTable.filters['CrimeTypeName'];
      this.dataTable._filter();
    } else {
      this.dataTable.filter((<HTMLSelectElement>evt.target).value, 'CrimeTypeName', 'equals');
    }
    this.dataTable.saveState();
  }

  public restoreEventsGridState(evt: TableState): void {
    if (this.dataTable && evt.filters && evt.filters['CrimeTypeName']) {
      let filterEl: HTMLSelectElement = this.dataTable.el.nativeElement.ownerDocument.getElementById('ctfilter');
      if (filterEl) {
        filterEl.value = (<FilterMetadata>evt.filters['CrimeTypeName']).value;
      }
    }
  }
}
